import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { AdminList, AdminListRow } from "@/modules/shell/AdminList";
import { AdvancedSection } from "@/modules/shell/AdvancedSection";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import { FieldHelp } from "@/components/ui/field-help";
import { orgSettingsHelp } from "@/modules/core/help/org-settings-help";
import {
  deactivateOrgMember,
  getOrgSettings,
  inviteOrgMember,
  listOrgMembers,
  listOrgRoles,
  patchOrgSettings,
  reactivateOrgMember,
  removeOrgLogo,
  setMemberCredentials,
  setMemberExcepts,
  setMemberRoles,
  setRolePermissions,
  uploadOrgLogo,
  type OrgMember,
  type OrgRole,
  type PermissionCatalogItem,
} from "@/shared/api/org-settings";
import { apiBase } from "@/shared/api/client";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";

/** Owner/Manager: tax + discount defaults, invite users, assign roles */
export function OrgSettingsPage() {
  const { companySlug } = useParams();
  const confirmUi = useConfirm();
  const queryClient = useQueryClient();
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [draftPerms, setDraftPerms] = useState<Set<string>>(new Set());
  const [taxPriceMode, setTaxPriceMode] = useState<"inclusive" | "exclusive">(
    "inclusive",
  );
  const [taxRatePct, setTaxRatePct] = useState("12");
  const [discountSoftCapPct, setDiscountSoftCapPct] = useState("10");
  const [allowOversell, setAllowOversell] = useState(true);
  const [requireSalesInvoices, setRequireSalesInvoices] = useState(false);
  const [requirePurchaseInvoices, setRequirePurchaseInvoices] = useState(false);
  const [requireSalesInTransit, setRequireSalesInTransit] = useState(false);
  const [docDateMaxBackDays, setDocDateMaxBackDays] = useState("");
  const [docDateBackdatePolicy, setDocDateBackdatePolicy] = useState<
    "soft" | "hard"
  >("soft");
  const [timezone, setTimezone] = useState("Asia/Manila");
  const [legalName, setLegalName] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [countryCode, setCountryCode] = useState("PH");
  const [tin, setTin] = useState("");
  const [orgPhone, setOrgPhone] = useState("");
  const [orgEmail, setOrgEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [registrationNote, setRegistrationNote] = useState("");
  const [permitNote, setPermitNote] = useState("");
  const [hasLogo, setHasLogo] = useState(false);
  const [logoBust, setLogoBust] = useState(0);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [invitePin, setInvitePin] = useState("");
  const [inviteRoleId, setInviteRoleId] = useState("");
  const [pinDrafts, setPinDrafts] = useState<Record<string, string>>({});
  const [nameDrafts, setNameDrafts] = useState<Record<string, string>>({});
  const [handedCreds, setHandedCreds] = useState<{
    name: string;
    userCode: string;
    pinHint: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [roleDraftReady, setRoleDraftReady] = useState(false);

  const settingsQuery = useQuery({
    queryKey: queryKeys.org.settings(companySlug ?? ""),
    enabled: Boolean(companySlug),
    queryFn: () => getOrgSettings(companySlug!),
  });
  const rolesQuery = useQuery({
    queryKey: queryKeys.org.roles(companySlug ?? ""),
    enabled: Boolean(companySlug),
    queryFn: () => listOrgRoles(companySlug!),
  });
  const membersQuery = useQuery({
    queryKey: queryKeys.org.members(companySlug ?? ""),
    enabled: Boolean(companySlug),
    queryFn: () => listOrgMembers(companySlug!),
  });
  const errorMsg = queryErrorMessage(
    settingsQuery.error ?? rolesQuery.error ?? membersQuery.error,
    settingsQuery.isError || rolesQuery.isError || membersQuery.isError,
  );
  useAuthRedirectOnQueryError(errorMsg);

  const org = settingsQuery.data?.org ?? null;
  const roles = rolesQuery.data?.roles ?? [];
  const catalog = rolesQuery.data?.catalog ?? [];
  const members = membersQuery.data?.members ?? [];
  const exceptable: PermissionCatalogItem[] =
    membersQuery.data?.exceptable ?? [];

  function loadRoleDraft(role: OrgRole | undefined) {
    if (!role) {
      setSelectedRoleId("");
      setDraftPerms(new Set());
      return;
    }
    setSelectedRoleId(role.id);
    setDraftPerms(new Set(role.permissions ?? []));
  }

  useEffect(() => {
    if (!org) return;
    setTaxPriceMode(org.taxPriceMode);
    setTaxRatePct(String(Number(org.taxRatePct)));
    setDiscountSoftCapPct(String(org.discountSoftCapPct));
    setAllowOversell(org.allowOversell !== false);
    setRequireSalesInvoices(Boolean(org.requireSalesInvoices));
    setRequirePurchaseInvoices(Boolean(org.requirePurchaseInvoices));
    setRequireSalesInTransit(Boolean(org.requireSalesInTransit));
    setDocDateMaxBackDays(
      org.docDateMaxBackDays == null ? "" : String(org.docDateMaxBackDays),
    );
    setDocDateBackdatePolicy(org.docDateBackdatePolicy ?? "soft");
    setTimezone(org.timezone);
    setLegalName(org.legalName ?? "");
    setAddressLine1(org.addressLine1 ?? "");
    setAddressLine2(org.addressLine2 ?? "");
    setCity(org.city ?? "");
    setProvince(org.province ?? "");
    setPostalCode(org.postalCode ?? "");
    setCountryCode(org.countryCode ?? "PH");
    setTin(org.tin ?? "");
    setOrgPhone(org.phone ?? "");
    setOrgEmail(org.email ?? "");
    setWebsite(org.website ?? "");
    setRegistrationNote(org.registrationNote ?? "");
    setPermitNote(org.permitNote ?? "");
    setHasLogo(Boolean(org.hasLogo));
  }, [org]);

  useEffect(() => {
    if (!roles.length) return;
    if (!roleDraftReady) {
      const keep =
        roles.find((x) => x.id === selectedRoleId) ??
        roles.find((x) => x.code === "cashier") ??
        roles[0];
      loadRoleDraft(keep);
      setRoleDraftReady(true);
    }
    if (!inviteRoleId) {
      const cashier = roles.find((x) => x.code === "cashier");
      if (cashier) setInviteRoleId(cashier.id);
      else if (roles[0]) setInviteRoleId(roles[0].id);
    }
  }, [roles, roleDraftReady, selectedRoleId, inviteRoleId]);

  /** Deep link from POS → People & PINs (plan/27 edit in context) */
  useEffect(() => {
    if (membersQuery.isPending) return;
    if (typeof window === "undefined") return;
    if (window.location.hash !== "#people") return;
    const el = document.getElementById("people");
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [membersQuery.isPending, members.length]);

  async function reload() {
    if (!companySlug) return;
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: queryKeys.org.settings(companySlug),
      }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.org.roles(companySlug),
      }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.org.members(companySlug),
      }),
    ]);
  }

  if (!companySlug) return null;

  const displayError =
    error ??
    (errorMsg && errorMsg !== "Not signed in" ? errorMsg : null);

  async function onSaveDefaults(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const { org: next } = await patchOrgSettings(companySlug!, {
        taxPriceMode,
        taxRatePct: Number(taxRatePct),
        discountSoftCapPct: Number(discountSoftCapPct),
        timezone,
      });
      setTimezone(next.timezone);
      setStatus("Defaults saved");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.org.settings(companySlug!),
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function onSaveLetterhead(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const { org: next } = await patchOrgSettings(companySlug!, {
        legalName: legalName || null,
        addressLine1: addressLine1 || null,
        addressLine2: addressLine2 || null,
        city: city || null,
        province: province || null,
        postalCode: postalCode || null,
        countryCode: countryCode || "PH",
        tin: tin || null,
        phone: orgPhone || null,
        email: orgEmail || null,
        website: website || null,
        registrationNote: registrationNote || null,
        permitNote: permitNote || null,
      });
      setLegalName(next.legalName ?? "");
      setAddressLine1(next.addressLine1 ?? "");
      setAddressLine2(next.addressLine2 ?? "");
      setCity(next.city ?? "");
      setProvince(next.province ?? "");
      setPostalCode(next.postalCode ?? "");
      setCountryCode(next.countryCode ?? "PH");
      setTin(next.tin ?? "");
      setOrgPhone(next.phone ?? "");
      setOrgEmail(next.email ?? "");
      setWebsite(next.website ?? "");
      setRegistrationNote(next.registrationNote ?? "");
      setPermitNote(next.permitNote ?? "");
      setHasLogo(Boolean(next.hasLogo));
      setStatus("Letterhead saved");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.org.settings(companySlug!),
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function onLogoSelected(file: File | null) {
    if (!file) return;
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      await uploadOrgLogo(companySlug!, file);
      setHasLogo(true);
      setLogoBust((n) => n + 1);
      setStatus("Logo uploaded");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.org.settings(companySlug!),
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Logo upload failed");
    } finally {
      setBusy(false);
    }
  }

  async function onRemoveLogo() {
    const ok = await confirmUi({
      title: "Remove logo?",
      description: "Prints will show the company name without a logo.",
      confirmLabel: "Remove",
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await removeOrgLogo(companySlug!);
      setHasLogo(false);
      setLogoBust((n) => n + 1);
      setStatus("Logo removed");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.org.settings(companySlug!),
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Remove failed");
    } finally {
      setBusy(false);
    }
  }

  async function onSaveInventoryPolicy(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const { org: next } = await patchOrgSettings(companySlug!, {
        allowOversell,
      });
      setAllowOversell(next.allowOversell !== false);
      setStatus(
        next.allowOversell
          ? "Allow oversell is on"
          : "Allow oversell is off — stock-outs below on-hand are blocked",
      );
      await queryClient.invalidateQueries({
        queryKey: queryKeys.org.settings(companySlug!),
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function onSaveInvoicePolicy(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const { org: next } = await patchOrgSettings(companySlug!, {
        requireSalesInvoices,
        requirePurchaseInvoices,
      });
      setRequireSalesInvoices(Boolean(next.requireSalesInvoices));
      setRequirePurchaseInvoices(Boolean(next.requirePurchaseInvoices));
      setStatus(
        [
          next.requireSalesInvoices ? "SI required" : "SI optional",
          next.requirePurchaseInvoices ? "PI required" : "PI optional",
        ].join(" · "),
      );
      await queryClient.invalidateQueries({
        queryKey: queryKeys.org.settings(companySlug!),
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function onSaveFulfillmentPolicy(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const { org: next } = await patchOrgSettings(companySlug!, {
        requireSalesInTransit,
      });
      setRequireSalesInTransit(Boolean(next.requireSalesInTransit));
      setStatus(
        next.requireSalesInTransit
          ? "In-transit required before deliver"
          : "In-transit optional",
      );
      await queryClient.invalidateQueries({
        queryKey: queryKeys.org.settings(companySlug!),
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function onSaveDocDatePolicy(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const trimmed = docDateMaxBackDays.trim();
      const maxDays =
        trimmed === ""
          ? null
          : (() => {
              const n = Number(trimmed);
              if (!Number.isInteger(n) || n < 0) {
                throw new Error("Max back days must be a whole number ≥ 0, or empty");
              }
              return n;
            })();
      const { org: next } = await patchOrgSettings(companySlug!, {
        docDateMaxBackDays: maxDays,
        docDateBackdatePolicy,
      });
      setDocDateMaxBackDays(
        next.docDateMaxBackDays == null
          ? ""
          : String(next.docDateMaxBackDays),
      );
      setDocDateBackdatePolicy(next.docDateBackdatePolicy ?? "soft");
      setStatus(
        next.docDateMaxBackDays == null
          ? "Doc date backdating uncapped"
          : `Doc date max ${next.docDateMaxBackDays} days back (${next.docDateBackdatePolicy})`,
      );
      await queryClient.invalidateQueries({
        queryKey: queryKeys.org.settings(companySlug!),
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function onSaveRolePermissions(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedRoleId) return;
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const { role } = await setRolePermissions(
        companySlug!,
        selectedRoleId,
        [...draftPerms],
      );
      setDraftPerms(new Set(role.permissions ?? []));
      setStatus(`Saved permissions for ${role.name}`);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.org.roles(companySlug!),
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  function togglePerm(key: string) {
    setDraftPerms((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function onInvite(e: React.FormEvent) {
    e.preventDefault();
    const email = inviteEmail.trim();
    const name = inviteName.trim();
    const pin = invitePin.trim();
    if (!email && (!name || pin.length < 4)) {
      setError(
        "Google email, or cashier name + PIN (min 4) — email is optional for POS",
      );
      return;
    }
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const { member } = await inviteOrgMember(companySlug!, {
        ...(email ? { email } : {}),
        ...(name ? { displayName: name } : {}),
        ...(inviteRoleId ? { roleId: inviteRoleId } : {}),
        ...(pin ? { pin } : {}),
      });
      setInviteEmail("");
      setInviteName("");
      setInvitePin("");
      if (member.userCode && pin) {
        const label = (member.displayName ?? name) || "Cashier";
        setHandedCreds({
          name: label,
          userCode: member.userCode,
          pinHint: pin,
        });
        setStatus(`Hand this to ${label}: user code + PIN`);
      } else if (member.userCode) {
        setHandedCreds(null);
        setStatus(
          `Added · user code ${member.userCode}${member.hasPasscode ? " · PIN set" : " · set PIN below"}`,
        );
      } else {
        setHandedCreds(null);
        setStatus("Invited — they sign in with Google on first visit");
      }
      reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Invite failed");
    } finally {
      setBusy(false);
    }
  }

  async function onSetPin(m: OrgMember) {
    const pin = (pinDrafts[m.userId] ?? "").trim();
    if (pin.length < 4) {
      setError("PIN must be at least 4 characters");
      return;
    }
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const { member } = await setMemberCredentials(companySlug!, m.userId, {
        pin,
        ...(m.userCode ? {} : { regenerateCode: true }),
      });
      setPinDrafts((prev) => ({ ...prev, [m.userId]: "" }));
      if (member.userCode) {
        setHandedCreds({
          name: m.displayName ?? m.email ?? "Cashier",
          userCode: member.userCode,
          pinHint: pin,
        });
      }
      setStatus(`PIN saved · user code ${member.userCode}`);
      reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  async function onSaveName(m: OrgMember) {
    const name = (nameDrafts[m.userId] ?? m.displayName ?? "").trim();
    if (!name) {
      setError("Display name required");
      return;
    }
    if (name === (m.displayName ?? "")) {
      setStatus("Name unchanged");
      return;
    }
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      await setMemberCredentials(companySlug!, m.userId, { displayName: name });
      setNameDrafts((prev) => {
        const next = { ...prev };
        delete next[m.userId];
        return next;
      });
      setStatus(`Name saved · ${name}`);
      reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  async function onRegenCode(m: OrgMember) {
    const label = m.displayName ?? m.email ?? "this member";
    const ok = await confirmUi({
      title: `Issue a new user code for ${label}?`,
      description:
        "Their current code stops working immediately. Hand them the new code before they next sign in on POS.",
      confirmLabel: "New code",
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const { member } = await setMemberCredentials(companySlug!, m.userId, {
        regenerateCode: true,
      });
      setStatus(`New user code ${member.userCode}`);
      reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  async function onRoleChange(member: OrgMember, roleId: string) {
    if (!roleId || roleId === (member.roles[0]?.id ?? "")) return;
    const label = member.displayName ?? member.email ?? "this member";
    const nextRole = roles.find((r) => r.id === roleId)?.name ?? "new role";
    const ok = await confirmUi({
      title: `Change ${label} to ${nextRole}?`,
      description:
        "Their permissions update immediately. Wrong role can lock them out of work they need.",
      confirmLabel: "Change role",
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      await setMemberRoles(companySlug!, member.userId, [roleId]);
      reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Update failed");
    } finally {
      setBusy(false);
    }
  }

  async function onExceptToggle(
    member: OrgMember,
    permission: string,
    checked: boolean,
    label: string,
  ) {
    const who = member.displayName ?? member.email ?? "this member";
    if (checked) {
      const ok = await confirmUi({
        title: `Block ${label} for ${who}?`,
        description:
          "Rare deny only. They lose this even if their role (or All actions *) allows it. Other access is unchanged. You can clear the except anytime.",
        confirmLabel: "Block this",
        destructive: true,
      });
      if (!ok) return;
    }
    const current = new Set(member.excepts ?? []);
    if (checked) current.add(permission);
    else current.delete(permission);
    const next = [...current];
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      await setMemberExcepts(companySlug!, member.userId, next);
      setStatus(
        checked
          ? `Except set · ${permission}`
          : `Except cleared · ${permission}`,
      );
      reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Update failed");
    } finally {
      setBusy(false);
    }
  }

  async function onDeactivate(m: OrgMember) {
    const label = m.displayName ?? m.email ?? "this member";
    const ok = await confirmUi({
      title: `Deactivate ${label}?`,
      description: "They will lose access immediately.",
      confirmLabel: "Deactivate",
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      await deactivateOrgMember(companySlug!, m.userId);
      setStatus(`${label} deactivated`);
      reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Deactivate failed");
    } finally {
      setBusy(false);
    }
  }

  async function onReactivate(m: OrgMember) {
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      await reactivateOrgMember(companySlug!, m.userId);
      setStatus(
        `${m.displayName ?? m.email ?? "Member"} reactivated`,
      );
      reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Reactivate failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <PageFrame width="narrow">
      <PageHeader
        title="Org settings"
        description="Desk staff: Google email. Floor cashiers: name + PIN only (no email). They use that user code + PIN on the register after a manager opens POS."
      />

      {org && (
        <p className="mt-2 text-sm text-[var(--color-muted)]">
          {org.name} · {org.timezone} · {org.currencyCode}
        </p>
      )}

      <form
        className="mt-8 space-y-3 border-t border-[var(--color-border)]/90 pt-6"
        onSubmit={(e) => void onSaveDefaults(e)}
      >
        <h2 className="m-0 font-[family-name:var(--font-display)] text-base font-semibold tracking-tight">
          Defaults
        </h2>
        <label className="block text-xs text-[var(--color-muted)]">
          Timezone
          <Select className="mt-1" value={timezone} onChange={(e) => setTimezone(e.target.value)}
          >
            <option value="Asia/Manila">Asia/Manila (PH)</option>
            <option value="Asia/Singapore">Asia/Singapore</option>
            <option value="Asia/Tokyo">Asia/Tokyo</option>
            <option value="UTC">UTC</option>
            <option value="America/Los_Angeles">America/Los_Angeles</option>
            <option value="America/New_York">America/New_York</option>
            <option value="Europe/London">Europe/London</option>
            {!["Asia/Manila", "Asia/Singapore", "Asia/Tokyo", "UTC", "America/Los_Angeles", "America/New_York", "Europe/London"].includes(
              timezone,
            ) && <option value={timezone}>{timezone}</option>}
          </Select>
        </label>
        <label className="block text-xs text-[var(--color-muted)]">
          <span className="inline-flex items-center">
            Tax price mode
            <FieldHelp content={orgSettingsHelp.taxPriceMode} />
          </span>
          <Select className="mt-1" value={taxPriceMode} onChange={(e) =>
              setTaxPriceMode(e.target.value as "inclusive" | "exclusive")
            }
          >
            <option value="inclusive">Inclusive (PH retail)</option>
            <option value="exclusive">Exclusive</option>
          </Select>
        </label>
        <label className="block text-xs text-[var(--color-muted)]">
          <span className="inline-flex items-center">
            Default tax rate %
            <FieldHelp content={orgSettingsHelp.taxRatePct} />
          </span>
          <Input className="mt-1 font-mono" inputMode="decimal" value={taxRatePct} onChange={(e) => setTaxRatePct(e.target.value)}
            required
            placeholder="12 or 0"
          />
        </label>
        <label className="block text-xs text-[var(--color-muted)]">
          <span className="inline-flex items-center">
            Discount soft cap %
            <FieldHelp content={orgSettingsHelp.discountSoftCapPct} />
          </span>
          <Input className="mt-1 font-mono" inputMode="numeric" value={discountSoftCapPct} onChange={(e) => setDiscountSoftCapPct(e.target.value)}
            required
          />
        </label>
        <Button type="submit" pending={busy} pendingLabel="Saving…">
          Save defaults
        </Button>
      </form>

      <form
        className="mt-10 space-y-3 border-t border-[var(--color-border)] pt-6"
        onSubmit={(e) => void onSaveLetterhead(e)}
      >
        <h2 className="m-0 inline-flex items-center gap-1 font-[family-name:var(--font-display)] text-base font-semibold tracking-tight">
          Letterhead
          <FieldHelp content={orgSettingsHelp.letterhead} />
        </h2>
        <p className="m-0 text-sm text-[var(--color-muted)]">
          Used on formal prints. Trade name is the company name above; legal name
          only if different.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          {hasLogo ? (
            <img
              src={`${apiBase()}/c/${companySlug}/settings/org/logo?v=${logoBust}`}
              alt="Company logo"
              className="h-14 w-auto max-w-[10rem] rounded-md border border-[var(--color-border)] bg-white object-contain p-1"
            />
          ) : (
            <div className="flex h-14 w-28 items-center justify-center rounded-md border border-dashed border-[var(--color-border)] text-[10px] text-[var(--color-muted)]">
              No logo
            </div>
          )}
          <label className="text-xs text-[var(--color-muted)]">
            <span className="sr-only">Upload logo</span>
            <Input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="max-w-xs text-xs"
              disabled={busy}
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                e.target.value = "";
                void onLogoSelected(f);
              }}
            />
          </label>
          {hasLogo ? (
            <Button
              type="button"
              variant="ghost"
              disabled={busy}
              onClick={() => void onRemoveLogo()}
            >
              Remove logo
            </Button>
          ) : null}
        </div>
        <label className="block text-xs text-[var(--color-muted)]">
          Legal name (optional)
          <Input
            className="mt-1"
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
            placeholder={org?.name}
          />
        </label>
        <label className="block text-xs text-[var(--color-muted)]">
          Address line 1
          <Input
            className="mt-1"
            value={addressLine1}
            onChange={(e) => setAddressLine1(e.target.value)}
          />
        </label>
        <label className="block text-xs text-[var(--color-muted)]">
          Address line 2
          <Input
            className="mt-1"
            value={addressLine2}
            onChange={(e) => setAddressLine2(e.target.value)}
          />
        </label>
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="block text-xs text-[var(--color-muted)]">
            City
            <Input
              className="mt-1"
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
          </label>
          <label className="block text-xs text-[var(--color-muted)]">
            Province
            <Input
              className="mt-1"
              value={province}
              onChange={(e) => setProvince(e.target.value)}
            />
          </label>
          <label className="block text-xs text-[var(--color-muted)]">
            Postal code
            <Input
              className="mt-1"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
            />
          </label>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-xs text-[var(--color-muted)]">
            TIN
            <Input
              className="mt-1 font-mono"
              value={tin}
              onChange={(e) => setTin(e.target.value)}
            />
          </label>
          <label className="block text-xs text-[var(--color-muted)]">
            Country
            <Input
              className="mt-1 font-mono uppercase"
              value={countryCode}
              maxLength={2}
              onChange={(e) => setCountryCode(e.target.value.toUpperCase())}
            />
          </label>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-xs text-[var(--color-muted)]">
            Phone
            <Input
              className="mt-1"
              value={orgPhone}
              onChange={(e) => setOrgPhone(e.target.value)}
            />
          </label>
          <label className="block text-xs text-[var(--color-muted)]">
            Email
            <Input
              className="mt-1"
              type="email"
              value={orgEmail}
              onChange={(e) => setOrgEmail(e.target.value)}
            />
          </label>
        </div>
        <label className="block text-xs text-[var(--color-muted)]">
          Website
          <Input
            className="mt-1"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </label>
        <AdvancedSection title="Registration & permit notes">
          <label className="block text-xs text-[var(--color-muted)]">
            Registration / business style note
            <Input
              className="mt-1"
              value={registrationNote}
              onChange={(e) => setRegistrationNote(e.target.value)}
            />
          </label>
          <label className="mt-3 block text-xs text-[var(--color-muted)]">
            Authority to print / permit note
            <Input
              className="mt-1"
              value={permitNote}
              onChange={(e) => setPermitNote(e.target.value)}
            />
          </label>
        </AdvancedSection>
        <Button type="submit" pending={busy} pendingLabel="Saving…">
          Save letterhead
        </Button>
      </form>

      <form
        className="mt-10 space-y-3 border-t border-[var(--color-border)] pt-6"
        onSubmit={(e) => void onSaveRolePermissions(e)}
      >
        <h2 className="m-0 font-[family-name:var(--font-display)] text-base font-semibold tracking-tight">
          Role permissions
        </h2>
        <p className="m-0 text-sm text-[var(--color-muted)]">
          Action = do without asking. Override allow = clear soft limits /
          approve others for that key. Owner must keep *.
        </p>
        <label className="block text-xs text-[var(--color-muted)]">
          Role
          <Select className="mt-1" value={selectedRoleId} disabled={busy || roles.length === 0} onChange={(e) => {
              const role = roles.find((r) => r.id === e.target.value);
              loadRoleDraft(role);
            }}
          >
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
                {r.code === "owner" ? " (*)" : ""}
              </option>
            ))}
          </Select>
        </label>
        {catalog.length > 0 && selectedRoleId ? (
          <div className="max-h-72 space-y-1 overflow-y-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-card)]/70 p-3">
            {(["action", "override", "meta"] as const).map((group) => {
              const items = catalog.filter((c) => c.group === group);
              if (items.length === 0) return null;
              const heading =
                group === "action"
                  ? "Actions"
                  : group === "override"
                    ? "Override allow"
                    : "Meta";
              return (
                <div key={group} className="mb-3 last:mb-0">
                  <div className="mb-1.5 text-sm font-medium text-[var(--color-foreground)]">
                    {heading}
                  </div>
                  {items.map((item) => (
                    <label
                      key={item.key}
                      className="flex items-start gap-2 py-0.5 text-sm"
                    >
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={draftPerms.has(item.key)}
                        disabled={busy}
                        onChange={() => togglePerm(item.key)}
                      />
                      <span>
                        {item.label}
                        <span className="ml-1 font-mono text-[10px] text-[var(--color-muted)]">
                          {item.key}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              );
            })}
          </div>
        ) : null}
        <Button
          type="submit"
          pending={busy}
          pendingLabel="Saving…"
          disabled={!selectedRoleId}
        >
          Save role permissions
        </Button>
      </form>

      <section
        id="people"
        className="mt-10 scroll-mt-24 space-y-3 border-t border-[var(--color-border)] pt-6"
      >
      <form
        className="space-y-3"
        onSubmit={(e) => void onInvite(e)}
      >
        <h2 className="m-0 font-[family-name:var(--font-display)] text-base font-semibold tracking-tight">
          People & PINs
        </h2>
        <p className="m-0 text-xs text-[var(--color-muted)]">
          Add cashiers (name + PIN) or desk users (Google email). Set or change
          PIN on each person below — same place POS links to.
        </p>
        <label className="block text-xs text-[var(--color-muted)]">
          Display name
          <Input type="text" className="mt-1" value={inviteName} onChange={(e) => setInviteName(e.target.value)}
            placeholder="Maya"
            autoComplete="off"
          />
        </label>
        <label className="block text-xs text-[var(--color-muted)]">
          Google email (optional — skip for PIN-only cashier)
          <Input type="email" className="mt-1" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="owner@shop.ph"
          />
        </label>
        <label className="block text-xs text-[var(--color-muted)]">
          Role
          <Select className="mt-1" value={inviteRoleId} onChange={(e) => setInviteRoleId(e.target.value)}
          >
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </Select>
        </label>
        <label className="block text-xs text-[var(--color-muted)]">
          POS PIN {inviteEmail.trim() ? "(optional)" : "(required without email)"}
          <Input type="password" inputMode="numeric" className="mt-1 font-mono" value={invitePin} onChange={(e) => setInvitePin(e.target.value)}
            placeholder="••••"
            minLength={4}
          />
        </label>
        <Button
          type="submit"
          pending={busy}
          pendingLabel="Adding…"
          disabled={
            !inviteEmail.trim() &&
            (!inviteName.trim() || invitePin.trim().length < 4)
          }
        >
          Add
        </Button>
      </form>

      {handedCreds && (
        <div className="mt-6 rounded-lg border border-[var(--color-primary)]/40 bg-[var(--color-card)] px-4 py-3">
          <div className="text-sm font-semibold">Give to cashier</div>
          <p className="mt-1 text-xs text-[var(--color-muted)]">
            {handedCreds.name} — shown once here; PIN is not stored in plain
            text after this. Manager opens the terminal with Google, then
            cashier uses code + PIN.
          </p>
          <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
            <dt className="text-[var(--color-muted)]">User code</dt>
            <dd className="m-0 font-mono text-lg tracking-widest">
              {handedCreds.userCode}
            </dd>
            <dt className="text-[var(--color-muted)]">PIN</dt>
            <dd className="m-0 font-mono text-lg tracking-widest">
              {handedCreds.pinHint}
            </dd>
          </dl>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                const text = `iCraftX POS\n${handedCreds.name}\nUser code: ${handedCreds.userCode}\nPIN: ${handedCreds.pinHint}`;
                void navigator.clipboard?.writeText(text).then(
                  () => setStatus("Copied user code + PIN"),
                  () => setStatus("Copy unavailable — write it down"),
                );
              }}
            >
              Copy
            </Button>
            <Button type="button" size="sm" asChild>
              <Link
                to={`/c/${companySlug}/pos`}
                className="no-underline"
              >
                Next: Open POS
              </Link>
            </Button>
          </div>
        </div>
      )}

      <div className="mt-6">
      <AdminList>
        {members.map((m) => {
          const deactivated = m.deactivatedAt != null;
          return (
          <AdminListRow
            key={m.userId}
            className="flex flex-col items-stretch gap-2 px-4 py-3.5"
          >
            <div className="font-medium">
              {m.displayName ?? m.email ?? m.userId.slice(0, 8)}
            </div>
            <div className="text-xs text-[var(--color-muted)]">
              {m.email ?? "PIN-only (no email)"}
              {m.pendingInvite ? " · pending Google" : ""}
              {m.userCode ? ` · user code ${m.userCode}` : " · no user code"}
              {m.hasPasscode ? " · PIN set" : " · no PIN"}
              {deactivated ? " · deactivated" : ""}
            </div>
            <label className="block text-xs text-[var(--color-muted)]">
              Role
              <Select className="mt-1" value={m.roles[0]?.id ?? ""} disabled={busy || deactivated} onChange={(e) => void onRoleChange(m, e.target.value)}
              >
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </Select>
            </label>
            {!deactivated && exceptable.length > 0 ? (
              <AdvancedSection
                className="mt-2"
                title={
                  (m.excepts?.length ?? 0) > 0
                    ? `Permission excepts (${m.excepts!.length})`
                    : "Permission excepts"
                }
                defaultOpen={(m.excepts?.length ?? 0) > 0}
              >
                <fieldset className="m-0 space-y-1.5 border-0 p-0">
                  <legend className="sr-only">Permission excepts</legend>
                  <p className="m-0 text-[11px] text-[var(--color-muted)]">
                    Optional denies for sensitive fields only. Checking a box{" "}
                    <strong className="font-medium text-[var(--color-foreground)]">
                      blocks
                    </strong>{" "}
                    that access — it does not grant anything. Other permissions
                    stay as their role defines.
                  </p>
                  {exceptable.map((item) => {
                    const on = (m.excepts ?? []).includes(item.key);
                    return (
                      <label
                        key={item.key}
                        className="flex items-start gap-2 text-xs text-[var(--color-foreground)]"
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={on}
                          disabled={busy}
                          onChange={(e) =>
                            void onExceptToggle(
                              m,
                              item.key,
                              e.target.checked,
                              item.label,
                            )
                          }
                        />
                        <span>
                          <span className="font-medium">
                            Block · {item.label}
                          </span>
                          <span className="mt-0.5 block font-mono text-[10px] text-[var(--color-muted)]">
                            {item.key}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </fieldset>
              </AdvancedSection>
            ) : null}
            {!deactivated && (
            <div className="space-y-2">
              <div className="flex flex-wrap items-end gap-2">
                <label className="min-w-0 flex-1 text-xs text-[var(--color-muted)]">
                  Display name
                  <Input
                    type="text"
                    className="mt-1"
                    value={nameDrafts[m.userId] ?? m.displayName ?? ""}
                    onChange={(e) =>
                      setNameDrafts((prev) => ({
                        ...prev,
                        [m.userId]: e.target.value,
                      }))
                    }
                    placeholder="Maya"
                    autoComplete="off"
                  />
                </label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => void onSaveName(m)}
                >
                  Save name
                </Button>
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <label className="min-w-0 flex-1 text-xs text-[var(--color-muted)]">
                  Set PIN
                  <Input type="password" inputMode="numeric" className="mt-1 font-mono" value={pinDrafts[m.userId] ?? ""} onChange={(e) =>
                      setPinDrafts((prev) => ({
                        ...prev,
                        [m.userId]: e.target.value,
                      }))
                    }
                    placeholder="••••"
                  />
                </label>
                <Button
                  type="button"
                  size="sm"
                  disabled={busy}
                  onClick={() => void onSetPin(m)}
                >
                  Save PIN
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => void onRegenCode(m)}
                >
                  New code
                </Button>
              </div>
            </div>
            )}
            <div className="flex flex-wrap gap-1">
              {!deactivated && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => void onDeactivate(m)}
                >
                  Deactivate
                </Button>
              )}
              {deactivated && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => void onReactivate(m)}
                >
                  Reactivate
                </Button>
              )}
            </div>
          </AdminListRow>
          );
        })}
      </AdminList>
      </div>
      </section>

      {status && (
        <Alert variant="muted" className="mt-4">
          {status}
        </Alert>
      )}
      {displayError && (
        <Alert variant="destructive" className="mt-4">
          {displayError}
        </Alert>
      )}

      <AdvancedSection title="Advanced">
        <p className="m-0 text-sm text-[var(--color-muted)]">
          Quieter company tools — pay methods, lists, numbers, branches.
        </p>
        <form
          className="space-y-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-background)]/60 px-3.5 py-3"
          onSubmit={(e) => void onSaveInventoryPolicy(e)}
        >
          <h3 className="m-0 text-sm font-medium text-[var(--color-foreground)]">
            Inventory policy
          </h3>
          <label className="flex items-start gap-2.5 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              checked={allowOversell}
              disabled={busy}
              onChange={(e) => setAllowOversell(e.target.checked)}
            />
            <span>
              <span className="inline-flex items-center">
                Allow selling / delivering below recorded on-hand
                <FieldHelp content={orgSettingsHelp.allowOversell} />
              </span>
              <span className="mt-0.5 block text-xs text-[var(--color-muted)]">
                On (default): soft warn + proceed — books often lag the floor.
                Off: hard-block stock-outs that would go negative.
              </span>
            </span>
          </label>
          <Button type="submit" size="sm" pending={busy} pendingLabel="Saving…">
            Save inventory policy
          </Button>
        </form>
        <form
          className="space-y-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-background)]/60 px-3.5 py-3"
          onSubmit={(e) => void onSaveInvoicePolicy(e)}
        >
          <h3 className="m-0 inline-flex items-center text-sm font-medium text-[var(--color-foreground)]">
            Invoices
            <FieldHelp content={orgSettingsHelp.requireSalesInvoices} />
          </h3>
          <label className="flex items-start gap-2.5 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              checked={requireSalesInvoices}
              disabled={busy}
              onChange={(e) => setRequireSalesInvoices(e.target.checked)}
            />
            <span>
              Require sales invoices for collect
              <span className="mt-0.5 block text-xs text-[var(--color-muted)]">
                Off (default): collect on the sales order. On: collect must
                target an open SI (managers with override can bypass).
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2.5 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              checked={requirePurchaseInvoices}
              disabled={busy}
              onChange={(e) => setRequirePurchaseInvoices(e.target.checked)}
            />
            <span>
              Require purchase invoices for pay
              <span className="mt-0.5 block text-xs text-[var(--color-muted)]">
                Off (default): pay on the purchase order. On: pay must target
                an open PI (managers with override can bypass).
              </span>
            </span>
          </label>
          <Button type="submit" size="sm" pending={busy} pendingLabel="Saving…">
            Save invoice policy
          </Button>
        </form>
        <form
          className="space-y-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-background)]/60 px-3.5 py-3"
          onSubmit={(e) => void onSaveFulfillmentPolicy(e)}
        >
          <h3 className="m-0 inline-flex items-center text-sm font-medium text-[var(--color-foreground)]">
            Fulfillment
            <FieldHelp content={orgSettingsHelp.requireSalesInTransit} />
          </h3>
          <label className="flex items-start gap-2.5 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              checked={requireSalesInTransit}
              disabled={busy}
              onChange={(e) => setRequireSalesInTransit(e.target.checked)}
            />
            <span>
              Require in-transit before deliver
              <span className="mt-0.5 block text-xs text-[var(--color-muted)]">
                Off (default): deliver on the sales order. On: delivery /
                unset orders need an open in-transit first (managers can
                bypass). Pickup orders stay exempt.
              </span>
            </span>
          </label>
          <Button type="submit" size="sm" pending={busy} pendingLabel="Saving…">
            Save fulfillment policy
          </Button>
        </form>
        <form
          className="space-y-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-background)]/60 px-3.5 py-3"
          onSubmit={(e) => void onSaveDocDatePolicy(e)}
        >
          <h3 className="m-0 inline-flex items-center text-sm font-medium text-[var(--color-foreground)]">
            Document date
            <FieldHelp content={orgSettingsHelp.docDateBackdate} />
          </h3>
          <label className="block text-sm">
            Allow doc date up to N days back
            <Input
              className="mt-1 font-mono"
              inputMode="numeric"
              placeholder="Unlimited"
              value={docDateMaxBackDays}
              disabled={busy}
              onChange={(e) => setDocDateMaxBackDays(e.target.value)}
            />
            <span className="mt-0.5 block text-xs text-[var(--color-muted)]">
              Empty = no cap. Future dates are always blocked.
            </span>
          </label>
          <fieldset className="space-y-2 border-0 p-0">
            <legend className="text-sm">When over the cap</legend>
            <label className="flex items-start gap-2.5 text-sm">
              <input
                type="radio"
                className="mt-1"
                name="docDateBackdatePolicy"
                checked={docDateBackdatePolicy === "soft"}
                disabled={busy}
                onChange={() => setDocDateBackdatePolicy("soft")}
              />
              <span>
                Soft warn (default)
                <span className="mt-0.5 block text-xs text-[var(--color-muted)]">
                  Confirm to proceed past the max-back window.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2.5 text-sm">
              <input
                type="radio"
                className="mt-1"
                name="docDateBackdatePolicy"
                checked={docDateBackdatePolicy === "hard"}
                disabled={busy}
                onChange={() => setDocDateBackdatePolicy("hard")}
              />
              <span>
                Hard reject
                <span className="mt-0.5 block text-xs text-[var(--color-muted)]">
                  Block save; operator may raise an override request.
                </span>
              </span>
            </label>
          </fieldset>
          <Button type="submit" size="sm" pending={busy} pendingLabel="Saving…">
            Save document date policy
          </Button>
        </form>
        <ul className="m-0 grid list-none gap-2 p-0 sm:grid-cols-2">
          {[
            {
              to: `/c/${companySlug}/settings/pay-methods`,
              label: "Pay methods",
              hint: "Cash, card, GCash, bank · GL map",
            },
            {
              to: `/c/${companySlug}/settings/price-lists`,
              label: "Price lists",
              hint: "POS & wholesale lists",
            },
            {
              to: `/c/${companySlug}/gl/accounts`,
              label: "Chart of accounts",
              hint: "Rename or add accounts",
            },
            {
              to: `/c/${companySlug}/settings/doc-series`,
              label: "Document numbers",
              hint: "Auto / manual series",
            },
            {
              to: `/c/${companySlug}/settings/branches`,
              label: "Branches",
              hint: "Locations for this company",
            },
            {
              to: `/c/${companySlug}/settings/branches/access`,
              label: "Branch access",
              hint: "Who can work where",
            },
            {
              to: `/c/${companySlug}/warehouses`,
              label: "Warehouses",
              hint: "Stock locations",
            },
          ].map((item) => (
            <li key={item.to}>
              <Link
                to={item.to}
                className="block rounded-xl border border-[var(--color-border)] bg-[var(--color-background)]/60 px-3.5 py-3 no-underline transition-colors hover:border-[var(--color-muted-fg)]"
              >
                <span className="block text-sm font-medium text-[var(--color-foreground)]">
                  {item.label}
                </span>
                <span className="mt-0.5 block text-xs text-[var(--color-muted)]">
                  {item.hint}
                </span>
              </Link>
            </li>
          ))}
        </ul>
        <dl className="m-0 space-y-1 border-t border-[var(--color-border)] pt-4 text-xs text-[var(--color-muted)]">
          <div className="flex justify-between gap-4">
            <dt>App version</dt>
            <dd className="m-0 font-mono text-[var(--color-foreground)]">
              {import.meta.env.VITE_APP_VERSION ?? "dev"}
            </dd>
          </div>
          {import.meta.env.VITE_APP_BUILT_AT ? (
            <div className="flex justify-between gap-4">
              <dt>Built</dt>
              <dd className="m-0 font-mono">
                {new Date(import.meta.env.VITE_APP_BUILT_AT).toLocaleString(
                  "en-PH",
                  { timeZone: "Asia/Manila" },
                )}{" "}
                (Asia/Manila)
              </dd>
            </div>
          ) : null}
          <div className="flex justify-between gap-4">
            <dt>Emergency builds</dt>
            <dd className="m-0">
              <a
                href="/versions.json"
                className="text-[var(--color-primary)] no-underline"
              >
                versions.json
              </a>
            </dd>
          </div>
        </dl>
      </AdvancedSection>

    </PageFrame>
  );
}
